import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth/authService';

export const Debug: React.FC = () => {
  const { user, isAuthenticated, permissions, hasRole, hasPermission, checkAuth } = useAuth();
  const [testResults, setTestResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const refreshUserData = async () => {
    setLoading(true);
    try {
      await checkAuth();

    } catch (error) {
      console.error('Error actualizando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const simulateLogin = async () => {
    setLoading(true);
    try {
      
      
    } catch (error) {
      console.error('Error en simulación:', error);
    } finally {
      setLoading(false);
    }
  };

  const testPermissionsEndpoint = async () => {
    setLoading(true);
    setTestResults(null);
    
    try {

      
      if (!user?.roleId) {
        setTestResults({ error: 'No hay roleId en el usuario' });
        return;
      }

      // Probar endpoint de permisos
      const response = await authService.getUserRole(user.roleId);

      
      setTestResults({
        success: true,
        roleId: user.roleId,
        roleName: response.name,
        permissions: response.permissions,
        totalPermissions: response.permissions.length
      });
      
    } catch (error) {
      console.error('Error en prueba:', error);
      setTestResults({
        error: true,
        message: error instanceof Error ? error.message : 'Error desconocido',
        details: error
      });
    } finally {
      setLoading(false);
    }
  };

  const testGetCurrentUser = async () => {
    setLoading(true);
    setTestResults(null);
    
    try {

      // Probar getCurrentUser
      const currentUser = await authService.getCurrentUser();
      
      setTestResults({
        success: true,
        type: 'getCurrentUser',
        user: currentUser,
        permissions: currentUser.permissions,
        totalPermissions: currentUser.permissions.length,
        roleId: currentUser.roleId,
        roleName: currentUser.roleName
      });
      
    } catch (error) {
      console.error('🧪 Error en getCurrentUser:', error);
      setTestResults({
        error: true,
        type: 'getCurrentUser',
        message: error instanceof Error ? error.message : 'Error desconocido',
        details: error
      });
    } finally {
      setLoading(false);
    }
  };

  const testDirectPermissionsCall = async () => {
    setLoading(true);
    setTestResults(null);
    
    try {

      if (!user?.roleId) {
        setTestResults({ error: 'No hay roleId en el usuario' });
        return;
      }

      // Llamada directa al endpoint sin usar authService
      const response = await fetch(`/api/permissions/role/${user.roleId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${data.error || 'Error desconocido'}`);
      }
      
      setTestResults({
        success: true,
        type: 'directPermissions',
        status: response.status,
        data: data,
        permissions: data.permissions,
        totalPermissions: data.permissions?.length || 0
      });
      
    } catch (error) {
      console.error('🧪 Error en llamada directa:', error);
      setTestResults({
        error: true,
        type: 'directPermissions',
        message: error instanceof Error ? error.message : 'Error desconocido',
        details: error
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Debug Information</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Información de Autenticación */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Authentication Status</h2>
            <div className="space-y-2">
              <p><strong>Is Authenticated:</strong> {isAuthenticated ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Token:</strong> {localStorage.getItem('token') ? '✅ Present' : '❌ Missing'}</p>
              <p><strong>User in localStorage:</strong> {localStorage.getItem('user') ? '✅ Present' : '❌ Missing'}</p>
              <p><strong>Role in localStorage:</strong> {localStorage.getItem('userRole') || '❌ Missing'}</p>
              <p><strong>Permissions in localStorage:</strong> {localStorage.getItem('userPermissions') || '❌ Missing'}</p>
            </div>
          </div>

          {/* Información del Usuario */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">User Information</h2>
            {user ? (
              <div className="space-y-2">
                <p><strong>ID:</strong> {user.id}</p>
                <p><strong>Name:</strong> {user.name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Role:</strong> {user.role}</p>
                <p><strong>Role ID:</strong> {user.roleId}</p>
                <p><strong>Role Name:</strong> {user.roleName}</p>
                <p><strong>Status:</strong> {user.status}</p>
                <p><strong>Last Login:</strong> {user.lastLogin || 'N/A'}</p>
              </div>
            ) : (
              <p className="text-red-500">❌ No user data available</p>
            )}
          </div>

          {/* Permisos */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Permissions</h2>
            {permissions.length > 0 ? (
              <div className="space-y-1">
                {permissions.map((permission, index) => (
                  <div key={index} className="bg-green-100 p-2 rounded text-sm">
                    ✅ {permission}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-yellow-500">⚠️ No permissions assigned</p>
            )}
          </div>

          {/* Tests de Roles */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Role Tests</h2>
            <div className="space-y-2">
              <p><strong>Has Admin Role:</strong> {hasRole('admin') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Has Superadmin Role:</strong> {hasRole('superadmin') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Has Manager Role:</strong> {hasRole('user') ? '✅ Yes' : '❌ No'}</p>
            </div>
          </div>

          {/* Tests de Permisos */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Permission Tests</h2>
            <div className="space-y-2">
              <p><strong>Dashboard View:</strong> {hasPermission('dashboard.view') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Leads View:</strong> {hasPermission('leads.view') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Users View:</strong> {hasPermission('users.view') ? '✅ Yes' : '❌ No'}</p>
            </div>
          </div>

          {/* Prueba de Endpoint de Permisos */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Test Permissions Endpoint</h2>
            <div className="space-x-2 mb-4">
              <button
                onClick={testPermissionsEndpoint}
                disabled={loading}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Testing...' : 'Test /permissions/role/:id'}
              </button>
              <button
                onClick={testGetCurrentUser}
                disabled={loading}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
              >
                {loading ? 'Testing...' : 'Test getCurrentUser()'}
              </button>
              <button
                onClick={refreshUserData}
                disabled={loading}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh User Data'}
              </button>
            </div>
            <div className="space-x-2 mb-4">
              <button
                onClick={testDirectPermissionsCall}
                disabled={loading}
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 disabled:opacity-50"
              >
                {loading ? 'Testing...' : 'Test Direct API Call'}
              </button>
              <button
                onClick={simulateLogin}
                disabled={loading}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? 'Simulating...' : 'Simulate Login Process'}
              </button>
            </div>
            
            {testResults && (
              <div className="mt-4">
                {testResults.error ? (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <h3 className="font-bold">❌ Error:</h3>
                    <p>{testResults.message || testResults.error}</p>
                    <pre className="text-xs mt-2 bg-red-50 p-2 rounded overflow-auto">
                      {JSON.stringify(testResults.details, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    <h3 className="font-bold">✅ Success ({testResults.type || 'permissions/role'})</h3>
                    <p><strong>Role ID:</strong> {testResults.roleId}</p>
                    <p><strong>Role Name:</strong> {testResults.roleName}</p>
                    <p><strong>Total Permissions:</strong> {testResults.totalPermissions}</p>
                    {testResults.type === 'getCurrentUser' && (
                      <div className="mt-2 p-2 bg-green-50 rounded">
                        <h4 className="font-semibold">User Data:</h4>
                        <p><strong>User ID:</strong> {testResults.user?.id}</p>
                        <p><strong>User Name:</strong> {testResults.user?.name}</p>
                        <p><strong>User Email:</strong> {testResults.user?.email}</p>
                      </div>
                    )}
                    {testResults.type === 'directPermissions' && (
                      <div className="mt-2 p-2 bg-green-50 rounded">
                        <h4 className="font-semibold">Direct API Response:</h4>
                        <p><strong>Status:</strong> {testResults.status}</p>
                        <p><strong>Response Type:</strong> Direct API Call</p>
                        <pre className="text-xs bg-green-100 p-2 rounded overflow-auto">
                          {JSON.stringify(testResults.data, null, 2)}
                        </pre>
                      </div>
                    )}
                    <div className="mt-2">
                      <h4 className="font-semibold">Permissions:</h4>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {testResults.permissions.map((perm: string, index: number) => (
                          <div key={index} className="bg-green-50 p-1 rounded">
                            {perm}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Datos Raw del localStorage */}
          <div className="bg-white p-6 rounded-lg shadow md:col-span-2">
            <h2 className="text-xl font-semibold mb-4">Raw localStorage Data</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">User JSON:</h3>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto">
                  {localStorage.getItem('user') || 'No data'}
                </pre>
              </div>
              <div>
                <h3 className="font-semibold">Permissions JSON:</h3>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto">
                  {localStorage.getItem('userPermissions') || 'No data'}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Debug;
